/**
 * A small pill of status text: "Drafting", "Recommended", "This device", "Coming".
 * `tone`: "neutral" (default) | "accent" | "warn" | "danger" | "success" | "editor".
 * `variant`: "soft" (tinted, default) | "outline" | "solid". Optional leading `icon`, or `dot`.
 * Colour never carries the meaning alone: the words do.
 * (cockatrice-ui `src/ui/Badge.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/badge.css';
import { mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface BadgeProps extends Attributes {
  /** Colour. Default "neutral". The words carry the meaning. */
  readonly tone?: 'neutral' | 'accent' | 'warn' | 'danger' | 'success' | 'editor' | undefined;
  /** Fill style. Default "soft". */
  readonly variant?: 'soft' | 'outline' | 'solid' | undefined;
  /** Leading icon name from Icon.jsx. */
  readonly icon?: IconName | undefined;
  /** Leading dot. */
  readonly dot?: boolean | undefined;
  /** 22 or 26px tall. Default "md". */
  readonly size?: 'md' | 'lg' | undefined;
  /** Text. */
  readonly children?: Child;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function Badge(props: BadgeProps): Child {
  const [local, rest] = splitProps(props, ['class', 'tone', 'variant', 'icon', 'dot', 'size', 'children']);
  const cls = (): string =>
    ['ui-badge', `ui-badge--${local.tone ?? 'neutral'}`, `ui-badge--${local.variant ?? 'soft'}`, local.size === 'lg' && 'ui-badge--lg', local.class].filter(Boolean).join(' ');
  return el(
    'span',
    mergeProps({ class: cls }, rest),
    when(
      () => local.dot === true,
      () => el('span', { class: 'ui-badge__dot', 'aria-hidden': 'true' }),
    ),
    when(
      () => local.icon !== undefined,
      () =>
        component(Icon, {
          get name() {
            return local.icon ?? 'check';
          },
          size: 'sm',
          class: 'ui-badge__icon',
        }),
    ),
    () => local.children,
  );
}
